import request from '@/utils/request';

// AI 接口超时时间单独设为 120 秒（大模型响应较慢）
const AI_TIMEOUT = 120000;

/** 步骤3：推荐模板 */
export function generateTemplate(data) {
    return request({ url: '/front/ai/template', method: 'post', data, timeout: AI_TIMEOUT });
}

/** 步骤5：生成故事背景 */
export function generateBackground(data) {
    return request({ url: '/front/ai/background', method: 'post', data, timeout: AI_TIMEOUT });
}

/** 步骤6：生成角色列表 */
export function generateRoles(data) {
    return request({ url: '/front/ai/roles', method: 'post', data, timeout: AI_TIMEOUT });
}

/** 步骤7：生成故事情节 */
export function generatePlot(data) {
    return request({ url: '/front/ai/plot', method: 'post', data, timeout: AI_TIMEOUT });
}

/** 步骤8辅助：生成标题建议 */
export function generateTitles(data) {
    return request({ url: '/front/ai/titles', method: 'post', data, timeout: AI_TIMEOUT });
}

/** 根据标题（和可选创作思路）生成作品简介 */
export function generateProjectIntro(data) {
    return request({ url: '/front/ai/project/intro', method: 'post', data, timeout: AI_TIMEOUT });
}

// ---- 长篇小说 AI 生成 ----

/** 生成世界观设定 */
export function generateWorldSetting(data) {
    return request({ url: '/front/ai/novel/world', method: 'post', data, timeout: AI_TIMEOUT });
}

/** 生成核心冲突 */
export function generateConflict(data) {
    return request({ url: '/front/ai/novel/conflict', method: 'post', data, timeout: AI_TIMEOUT });
}

/** 生成长篇角色阵容 */
export function generateLongNovelRoles(data) {
    return request({ url: '/front/ai/novel/roles', method: 'post', data, timeout: AI_TIMEOUT });
}

/** 生成三幕大纲 */
export function generateThreeActOutline(data) {
    return request({ url: '/front/ai/novel/outline', method: 'post', data, timeout: AI_TIMEOUT });
}

/** 生成分卷细纲 */
export function generateVolumeOutline(data) {
    return request({ url: '/front/ai/novel/volumes', method: 'post', data, timeout: AI_TIMEOUT });
}

/** 按三种命名策略生成长篇标题 */
export function generateLongNovelTitles(data) {
    return request({ url: '/front/ai/novel/titles', method: 'post', data, timeout: AI_TIMEOUT });
}

/** 根据章节摘要生成章节正文（普通模式，保留兼容） */
export function generateChapterContent(data) {
    return request({ url: '/front/ai/novel/chapter-content', method: 'post', data, timeout: AI_TIMEOUT });
}

/**
 * 通用 SSE 流式请求工具（内部复用）
 */
function createSseStream(url, data, onToken, onDone, onError) {
    const baseURL = process.env.VUE_APP_BASE_API || '/api';
    const token = localStorage.getItem('MIAOBI_TOKEN');
    const ctrl = new AbortController();

    fetch(`${baseURL}${url}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json;charset=utf-8',
            ...(token ? { satoken: token } : {})
        },
        body: JSON.stringify(data),
        signal: ctrl.signal
    }).then(res => {
        if (!res.ok) {
            onError && onError(`HTTP ${res.status}`);
            return;
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buf = '';

        function parseAndDispatch() {
            const blocks = buf.split('\n\n');
            buf = blocks.pop();
            for (const block of blocks) {
                if (!block.trim()) continue;
                let eventName = '';
                let dataLines = [];
                for (const line of block.split('\n')) {
                    if (line.startsWith('event:')) {
                        eventName = line.slice(6).trim();
                    } else if (line.startsWith('data:')) {
                        dataLines.push(line.slice(5).trimStart());
                    }
                }
                const payload = dataLines.join('\n');
                if (eventName === 'token') {
                    let tokenText = payload;
                    try {
                        const parsed = JSON.parse(payload);
                        if (parsed && typeof parsed.content === 'string') {
                            tokenText = parsed.content;
                        }
                    } catch (e) { /* 非 JSON 直接用原始文本 */ }
                    onToken && onToken(tokenText);
                } else if (eventName === 'done') {
                    onDone && onDone();
                } else if (eventName === 'error') {
                    onError && onError(payload);
                }
            }
        }

        function pump() {
            reader.read().then(({ done, value }) => {
                if (done) {
                    if (buf.trim()) parseAndDispatch();
                    onDone && onDone();
                    return;
                }
                buf += decoder.decode(value, { stream: true });
                parseAndDispatch();
                pump();
            }).catch(err => {
                if (err.name !== 'AbortError') {
                    onError && onError(err.message || '流式读取失败');
                }
            });
        }
        pump();
    }).catch(err => {
        if (err.name !== 'AbortError') {
            onError && onError(err.message || '连接失败');
        }
    });

    return () => ctrl.abort();
}

/**
 * 流式生成分卷细纲（SSE）
 * @param {Object} data - 请求参数
 * @param {Function} onToken - 每收到一段 token 时回调，参数为 token 字符串
 * @param {Function} onDone - 生成完成时回调
 * @param {Function} onError - 出错时回调，参数为错误信息字符串
 * @returns {Function} 调用返回值可取消请求
 */
export function streamVolumeOutline(data, onToken, onDone, onError) {
    return createSseStream('/front/ai/novel/volumes/stream', data, onToken, onDone, onError);
}

/**
 * 流式生成章节正文（SSE）
 * @param {Object} data - 请求参数
 * @param {Function} onToken - 每收到一个 token 时回调，参数为 token 字符串
 * @param {Function} onDone - 生成完成时回调
 * @param {Function} onError - 出错时回调，参数为错误信息字符串
 * @returns {Function} 调用返回值可取消请求
 */
export function streamChapterContent(data, onToken, onDone, onError) {
    return createSseStream('/front/ai/novel/chapter-content/stream', data, onToken, onDone, onError);
}

/**
 * 流式 AI 助手：续写 / 润色 / 改写（SSE）
 * @param {Object} data - 请求参数，含 assistType / selectedText / existingContent 等
 * @param {Function} onToken
 * @param {Function} onDone
 * @param {Function} onError
 * @returns {Function} 取消函数
 */
export function streamAssistContent(data, onToken, onDone, onError) {
    return createSseStream('/front/ai/novel/assist/stream', data, onToken, onDone, onError);
}
